import React from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import FileViewerModal from '../Dataroom2.0/FileViewerModal';
import { ObservationViewSidebar } from '../Observations';
import { GetIRLDocData } from '../../redux/documentsSlice';
import { GetInformationRequests } from '../../redux/questionBankSlice';
import { GetObservationsByScope } from '../../redux/meetingSlice';
import axiosInstance from '../../apiServices/axios';
import { SCHEDULER_OBSERVATION } from '../../apiServices/api_routes';
import SafeHTML from '../SafeHTML';
import styles from '../IRL/css/index.module.css';

/**
 * Hosts preview overlays for task linked records (VDR / IRL / Observation).
 */
const TaskReferencePreviews = ({ previewLink, onClose }) => {
    const dispatch = useDispatch();
    const { informationRequests } = useSelector(state => state.questionBank);
    const { workstreams } = useSelector(state => state.dataroom);
    const { scopeObservations } = useSelector(state => state.meeting);

    const [vdrFile, setVdrFile] = React.useState(null);
    const [vdrOpen, setVdrOpen] = React.useState(false);
    const [vdrWorkstream, setVdrWorkstream] = React.useState(null);

    const [irlRow, setIrlRow] = React.useState(null);
    const [irlOpen, setIrlOpen] = React.useState(false);

    const [obsRow, setObsRow] = React.useState(null);
    const [obsOpen, setObsOpen] = React.useState(false);

    React.useEffect(() => {
        if (!previewLink) return;
        let cancelled = false;

        const open = async () => {
            setVdrOpen(false);
            setVdrFile(null);
            setIrlOpen(false);
            setIrlRow(null);
            setObsOpen(false);
            setObsRow(null);

            try {
                if (previewLink.record_type === 'vdr_file') {
                    const wsId = previewLink.workstream_id;
                    if (!wsId) {
                        toast.error('Missing workstream for this file');
                        onClose?.();
                        return;
                    }
                    const res = await dispatch(
                        GetIRLDocData({ id: previewLink.record_id, workstream: wsId }),
                    );
                    if (cancelled) return;
                    if (res?.type !== 'IRLDOC/getIRLDocData/fulfilled') {
                        toast.error('Could not open VDR file');
                        onClose?.();
                        return;
                    }
                    const file = res.payload?.data;
                    const ws = (workstreams || []).find(w => Number(w.id) === Number(wsId));
                    setVdrWorkstream(ws || { id: wsId, name: previewLink.workstream_name || '' });
                    setVdrFile({
                        ...file,
                        filePath: [previewLink.workstream_name, previewLink.folder_name]
                            .filter(Boolean)
                            .join('/'),
                    });
                    setVdrOpen(true);
                    return;
                }

                if (previewLink.record_type === 'irl') {
                    let row = (informationRequests || []).find(
                        item => Number(item.id) === Number(previewLink.record_id),
                    );
                    if (!row) {
                        const fetched = await dispatch(GetInformationRequests());
                        const list = fetched?.payload?.data?.['information requests'] || [];
                        row = list.find(item => Number(item.id) === Number(previewLink.record_id));
                    }
                    if (!row) {
                        row = {
                            id: previewLink.record_id,
                            request: previewLink.display_subtitle || previewLink.display_label,
                            prefix: '',
                            tag: '',
                            item: '',
                            sub_item: '',
                            workstream: previewLink.workstream_id,
                        };
                    }
                    if (cancelled) return;
                    setIrlRow(row);
                    setIrlOpen(true);
                    return;
                }

                if (previewLink.record_type === 'observation') {
                    let row = (scopeObservations || []).find(
                        item => Number(item.id) === Number(previewLink.record_id),
                    );
                    if (!row) {
                        try {
                            const { data } = await axiosInstance.get(
                                `${SCHEDULER_OBSERVATION}${localStorage['project_id']}/${previewLink.record_id}/`,
                            );
                            row = data;
                        } catch {
                            await dispatch(GetObservationsByScope('all'));
                            row = {
                                id: previewLink.record_id,
                                observation: previewLink.display_label,
                                assessment: previewLink.assessment,
                                workstream_repr: previewLink.workstream_name,
                            };
                        }
                    }
                    if (cancelled) return;
                    setObsRow(row);
                    setObsOpen(true);
                }
            } catch {
                if (!cancelled) {
                    toast.error('Could not open reference');
                    onClose?.();
                }
            }
        };

        open();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewLink]);

    // Refresh IRL row from store once informationRequests load
    React.useEffect(() => {
        if (!previewLink || previewLink.record_type !== 'irl' || !irlOpen) return;
        const row = (informationRequests || []).find(
            item => Number(item.id) === Number(previewLink.record_id),
        );
        if (row) setIrlRow(row);
    }, [informationRequests, previewLink, irlOpen]);

    const handleCloseAll = () => {
        setVdrOpen(false);
        setVdrFile(null);
        setIrlOpen(false);
        setIrlRow(null);
        setObsOpen(false);
        setObsRow(null);
        onClose?.();
    };

    return (
        <>
            <FileViewerModal
                openModal={vdrOpen}
                setOpenModal={open => {
                    if (!open) handleCloseAll();
                    else setVdrOpen(open);
                }}
                selectedFile={vdrFile}
                setSelectedFile={setVdrFile}
                folderFiles={vdrFile ? [vdrFile] : []}
                selectedFileIndex={0}
                workstream={vdrWorkstream}
            />

            <Offcanvas placement="end" show={irlOpen} onHide={handleCloseAll}>
                <Offcanvas.Body>
                    <div className="mb-3">
                        <p className={`${styles.blueText} mb-1`}>
                            {(workstreams || []).find(ws => ws?.id === irlRow?.workstream)?.name ||
                                previewLink?.workstream_name ||
                                'IRL'}
                        </p>
                        <div className={styles.smallBlueLabel}>
                            {irlRow?.prefix != null
                                ? `${irlRow.prefix}${irlRow.tag || ''}.${irlRow.item}.${irlRow.sub_item}`
                                : previewLink?.irl_number || previewLink?.display_label}
                        </div>
                    </div>
                    <div className="mb-3">
                        <span className={styles.blueText}>Request</span>
                        <SafeHTML html={irlRow?.request || ''} className={styles.regularText} tag="p" />
                    </div>
                    {irlRow?.irresponse?.response ? (
                        <div className="mb-3">
                            <span className={styles.blueText}>Response</span>
                            <SafeHTML
                                html={irlRow.irresponse.response}
                                className={styles.regularText}
                                tag="p"
                            />
                        </div>
                    ) : null}
                    {irlRow?.priorities ? (
                        <div className="mb-2">
                            <span className={styles.blueText}>Priority: </span>
                            <span className={styles.regularText}>{irlRow.priorities}</span>
                        </div>
                    ) : null}
                    <button type="button" className="btn btn-secondary btn-sm mt-3" onClick={handleCloseAll}>
                        Close
                    </button>
                </Offcanvas.Body>
            </Offcanvas>

            <ObservationViewSidebar
                show={obsOpen}
                setShow={open => {
                    if (!open) handleCloseAll();
                }}
                selectedRow={obsRow}
                setSelectedRow={setObsRow}
                setObservationFormModal={() => {}}
                allData={obsRow ? [obsRow] : []}
            />
        </>
    );
};

export default TaskReferencePreviews;
